import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff, Shield } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

/*  ──────────────────────────────────────────────────────────
    Aipron DACAMS — Login Page (v3 Corporate Consulting)
    ────────────────────────────────────────────────────────── */

const TEXTS = {
  es: {
    tagline: 'Airport Intelligence Engine',
    subtitle: 'Demand & Capacity Analysis',
    email_label: 'Email Corporativo',
    email_placeholder: 'nombre@aipron.consulting',
    password_label: 'Contraseña',
    forgot: '¿Olvidó?',
    submit: 'Acceder al Sistema',
    loading: 'Validando...',
    error: 'Credenciales inválidas. Verifica tu email y contraseña.',
    forgot_prompt: 'Introduce tu email para recuperar la contraseña:',
    forgot_sent: 'Se ha enviado un enlace de restablecimiento a',
    copyright: '© 2026 Aipron Consulting. All rights reserved.',
  },
  en: {
    tagline: 'Airport Intelligence Engine',
    subtitle: 'Demand & Capacity Analysis',
    email_label: 'Corporate Email',
    email_placeholder: 'name@aipron.consulting',
    password_label: 'Password',
    forgot: 'Forgot?',
    submit: 'Access System',
    loading: 'Validating...',
    error: 'Invalid credentials. Please check your email and password.',
    forgot_prompt: 'Enter your email to recover your password:',
    forgot_sent: 'A password reset link has been sent to',
    copyright: '© 2026 Aipron Consulting. All rights reserved.',
  },
};

export const LoginPage = () => {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { theme } = useTheme();
  const t = TEXTS[(language as 'es' | 'en')] || TEXTS.en;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isAuthenticated) navigate('/home', { replace: true });
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/home');
    } catch {
      setError(t.error);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    const emailVal = email || prompt(t.forgot_prompt);
    if (emailVal) alert(`${t.forgot_sent} ${emailVal}`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>

      {/* Login Card */}
      <div className="w-full max-w-[420px] mx-4 animate-fade-in">
        <div className="rounded-xl overflow-hidden"
             style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)' }}>

          {/* Top accent line */}
          <div className="h-[2px]" style={{ background: 'var(--aipron-accent)' }} />

          <div className="p-8 pt-10">

            {/* Logo & Branding */}
            <div className="text-center mb-10">
              <img
                src={theme === 'dark' ? '/img/Logo_dark.png' : '/img/Logo_light.png'}
                alt="Aipron DACAMS"
                className="mx-auto"
                style={{ width: '62%', height: 'auto', objectFit: 'contain' }}
              />
              <h1 className="text-[11px] font-semibold uppercase tracking-[0.15em] mt-4"
                  style={{ color: 'var(--aipron-accent)' }}>
                {t.tagline}
              </h1>
              <p className="text-[11px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
                {t.subtitle}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">

              {/* Email Field */}
              <div className="space-y-1.5">
                <label htmlFor="login-email"
                       className="block text-[10px] font-semibold uppercase tracking-[0.1em] ml-px"
                       style={{ color: 'var(--text-tertiary)' }}>
                  {t.email_label}
                </label>
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field"
                  placeholder={t.email_placeholder}
                  required
                  autoComplete="email"
                />
              </div>

              {/* Password Field */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <label htmlFor="login-password"
                         className="block text-[10px] font-semibold uppercase tracking-[0.1em] ml-px"
                         style={{ color: 'var(--text-tertiary)' }}>
                    {t.password_label}
                  </label>
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="text-[11px] font-medium transition-colors duration-150"
                    style={{ color: 'var(--aipron-accent)' }}
                  >
                    {t.forgot}
                  </button>
                </div>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field pr-10"
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center transition-colors duration-150"
                    style={{ color: 'var(--text-tertiary)' }}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-start gap-2 p-3 rounded-lg animate-fade-in"
                     style={{ background: 'rgba(195, 51, 51, 0.06)', border: '1px solid rgba(195, 51, 51, 0.15)' }}
                     role="alert">
                  <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--aipron-danger)' }} />
                  <p className="text-xs" style={{ color: 'var(--aipron-danger)' }}>{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-3.5 mt-2 group/btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2.5">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t.loading}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    {t.submit}
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover/btn:translate-x-1" />
                  </span>
                )}
              </button>
            </form>
          </div>

          {/* Footer */}
          <div className="px-8 py-4 border-t" style={{ borderColor: 'var(--border-subtle)', background: 'var(--surface-raised)' }}>
            <p className="text-[10px] text-center" style={{ color: 'var(--text-tertiary)' }}>
              {t.copyright}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};